import { useContext } from "react";
import { useWatch } from "react-hook-form";
import { Alert, Box } from "@mui/material";
import { RecordContext } from "../../components/record/Record";
import { FormatedFields } from "../../types/fields.types";
import { Choice, getChoiceLabel } from "../../utils/choiceUtils";
import { hasValue } from "./scheduledTasksOptions";

// Live pt-PT recap of the schedule, built from the recurrence selections. The frequency decides
// which day fields matter; each due hour generates its own task on every run day.
export const ScheduleSummary = () => {
	const context = useContext(RecordContext);
	const entityForm = context?.entityForm;
	const item = context?.item;
	const formatedFields = context?.formatedFields;
	const isDetail = context?.type === "detail";

	useWatch({ control: entityForm?.control });
	const value = (name: string) => {
		if (isDetail) return item?.[name];
		const formValue = entityForm?.getValues(name);
		return formValue === undefined ? item?.[name] : formValue;
	};

	const frequency = value("run_at_frequency");

	let phrase = "";
	if (hasValue(frequency)) {
		phrase = buildSchedulePhrase(formatedFields, {
			frequency,
			weekdays: value("run_at_weekdays"),
			monthdays: value("run_at_monthdays"),
			months: value("run_at_months"),
			weekOrdinals: value("run_at_week_ordinals"),
			dueHours: value("due_hours")
		});
	}

	// Before a frequency is picked there is no phrase: render the same alert invisibly so the slot
	// keeps its height and the layout doesn't jump when the recap appears.
	return (
		<Box sx={{ px: 4, py: 2, backgroundColor: "#fff" }}>
			<Alert severity="info" sx={phrase ? undefined : { visibility: "hidden" }}>
				{phrase || "Selecione a Frequência."}
			</Alert>
		</Box>
	);
};

type ScheduleValues = {
	frequency: any;
	weekdays: any;
	monthdays: any;
	months: any;
	weekOrdinals: any;
	dueHours: any;
};

const toList = (value: any) => {
	if (!hasValue(value)) return [];
	const entries = Array.isArray(value) ? value : String(value).split(",");
	const list = entries.map((entry) => String(entry).trim()).filter((entry) => entry !== "");
	return list;
};

const getFieldChoices = (formatedFields: FormatedFields | undefined, fieldName: string) => {
	const field = formatedFields?.find((formatedField) => formatedField.field === fieldName);
	const choices = (field?.settings?.options?.choices ?? []) as Array<Choice>;
	return choices;
};

// Selections keep the order they were clicked in; display them in the field's choice order instead.
const sortedValuesByChoiceOrder = (formatedFields: FormatedFields | undefined, fieldName: string, value: any) => {
	const choices = getFieldChoices(formatedFields, fieldName);
	const orderOf = (entry: string) => {
		const index = choices.findIndex((choice) => choice.value === entry);
		return index === -1 ? Number.MAX_SAFE_INTEGER : index;
	};
	const sortedValues = [...toList(value)].sort((first, second) => orderOf(first) - orderOf(second));
	return sortedValues;
};

const getChoiceTexts = (formatedFields: FormatedFields | undefined, fieldName: string, value: any) => {
	const choices = getFieldChoices(formatedFields, fieldName);
	const texts = sortedValuesByChoiceOrder(formatedFields, fieldName, value).map((entry) =>
		getChoiceLabel(choices, entry)
	);
	return texts;
};

const joinWithAnd = (parts: Array<string>) => {
	if (parts.length <= 1) return parts[0] ?? "";
	const joined = `${parts.slice(0, -1).join(", ")} e ${parts[parts.length - 1]}`;
	return joined;
};

const WORKDAY_VALUES = ["1", "2", "3", "4", "5"];

// "feira" only reads correctly when every selected day is a workday (Sábado/Domingo aren't "-feira").
const weekdaysDisplay = (formatedFields: FormatedFields | undefined, weekdays: any) => {
	const weekdayValues = sortedValuesByChoiceOrder(formatedFields, "run_at_weekdays", weekdays);
	const weekdayTexts = getChoiceTexts(formatedFields, "run_at_weekdays", weekdays);
	if (!weekdayTexts.length) return `"Dia da Semana por Selecionar"`;
	const isEveryDayWorkday = weekdayValues.every((entry) => WORKDAY_VALUES.includes(entry));
	const display = `${joinWithAnd(weekdayTexts)}${isEveryDayWorkday ? "-feira" : ""}`;
	return display;
};

const monthdaysClause = (dayTexts: Array<string>) => {
	if (dayTexts.length === 0) return `no dia "Dia do Mês por Selecionar"`;
	const clause = dayTexts.length === 1 ? `no dia ${dayTexts[0]}` : `nos dias ${joinWithAnd(dayTexts)}`;
	return clause;
};

const frequencyClause = (formatedFields: FormatedFields | undefined, values: ScheduleValues) => {
	if (values.frequency === "daily") return "todos os dias";
	if (values.frequency === "daily_no_sunday") return "todos os dias exceto Domingo";

	if (values.frequency === "weekly") {
		return `em cada ${weekdaysDisplay(formatedFields, values.weekdays)}`;
	}

	if (values.frequency === "monthly") {
		const dayTexts = getChoiceTexts(formatedFields, "run_at_monthdays", values.monthdays);
		return `${monthdaysClause(dayTexts)} de cada mês`;
	}

	if (values.frequency === "monthly_week") {
		const ordinalTexts = getChoiceTexts(formatedFields, "run_at_week_ordinals", values.weekOrdinals);
		const ordinalsDisplay = ordinalTexts.length ? joinWithAnd(ordinalTexts) : `"Semana por Selecionar"`;
		return `à ${weekdaysDisplay(formatedFields, values.weekdays)} de todos os meses, na ${ordinalsDisplay} Semana do mês`;
	}

	if (values.frequency === "annual") {
		const monthTexts = getChoiceTexts(formatedFields, "run_at_months", values.months);
		const dayTexts = getChoiceTexts(formatedFields, "run_at_monthdays", values.monthdays);
		const monthsDisplay = monthTexts.length ? joinWithAnd(monthTexts) : `"Meses por Selecionar"`;
		return `${monthdaysClause(dayTexts)}, em ${monthsDisplay}, todos os anos`;
	}

	return "";
};

const dueHoursClause = (hourTexts: Array<string>) => {
	if (hourTexts.length === 0) return `com a deadline "Hora por Selecionar"`;
	const clause =
		hourTexts.length === 1
			? `com a deadline às ${hourTexts[0]}`
			: `com as respetivas deadlines às ${joinWithAnd(hourTexts)}`;
	return clause;
};

const buildSchedulePhrase = (formatedFields: FormatedFields | undefined, values: ScheduleValues) => {
	const dayClause = frequencyClause(formatedFields, values);
	if (!dayClause) return "";

	// One task is created per due hour on every run day, so multiple hours mean multiple tasks.
	// "por dia" is skipped on the daily frequencies, where "todos os dias" already says it.
	const hourTexts = getChoiceTexts(formatedFields, "due_hours", values.dueHours);
	const isDailyFrequency = values.frequency === "daily" || values.frequency === "daily_no_sunday";
	const perDaySuffix = isDailyFrequency ? "" : " por dia";
	const subject = hourTexts.length > 1 ? `Serão criadas ${hourTexts.length} Tarefas${perDaySuffix}` : "Uma Tarefa será criada";

	return `${subject} ${dayClause}, ${dueHoursClause(hourTexts)}.`;
};
